import { FailureRecord } from '@/types';

export interface WeibullParameters {
  shape: number; // β (beta) - Weibull shape parameter
  scale: number; // η (eta) - Weibull scale parameter
  rSquared: number; // Goodness of fit (linearized ln(t) vs ln(-ln(1-F)) space)
  adjustedRSquared?: number; // Sample-size-adjusted R² (backend-computed)
  rmse?: number; // Residual RMSE in linearized ln(-ln(1-F)) units (backend-computed)
  // Confidence intervals
  shapeCI?: { lower: number; upper: number };
  scaleCI?: { lower: number; upper: number };
}

export interface WeibullAnalysisResult {
  parameters: WeibullParameters;
  plotData: {
    operationTimes: number[];
    probabilities: number[];
    fittedLine: { x: number; y: number }[];
    failureRecords?: Array<{
      case_id: string;
      turbine_id: string;
      site_name: string;
      component_major: string;
      component_minor: string;
      failure_mode: string;
      outcome_name: string;
      failure_date: string;
      operation_time_years: number;
      turbine_model: string;
      turbine_name: string;
    }>;
    confidenceBands?: {
      lower: { x: number; y: number }[];
      upper: { x: number; y: number }[];
    };
  };
  summary: {
    sampleSize: number;
    meanLife: number;
    medianLife: number;
    characteristicLife: number;
  };
  statistics?: {
    standardErrors: { shape: number; scale: number };
    confidenceLevel: number;
    degreesOfFreedom: number;
  };
  mleResult?: import('./weibullMLE').MLEResult;
  forecastConstraints?: import('../types/vbl').VblForecastConstraints;
  metadata?: import('@/types/forecast').AnalysisMetadata;
  validation?: {
    isValid: boolean;
    warnings: string[];
    recommendations: string[];
  };
  onyxPlotData?: {
    operationTimes: number[];
    probabilities: number[];
    failureRecords?: Array<{
      case_id: string;
      turbine_id: string;
      site_name: string;
      component_major: string;
      component_minor: string;
      failure_mode: string;
      outcome_name: string;
      failure_date: string;
      operation_time_years: number;
      turbine_model: string;
      turbine_name: string;
    }>;
  };
}

// Helper functions
function mean(arr: number[]): number {
  return arr.reduce((sum, val) => sum + val, 0) / arr.length;
}

function min(arr: number[]): number {
  return Math.min(...arr);
}

function max(arr: number[]): number {
  return Math.max(...arr);
}

// Simplified gamma function approximation (Stirling's approximation)
function gamma(z: number): number {
  if (z < 0.5) {
    return Math.PI / (Math.sin(Math.PI * z) * gamma(1 - z));
  }
  z -= 1;
  const x =
    0.99999999999980993 +
    676.5203681218851 / (z + 1) +
    -1259.1392167224028 / (z + 2) +
    771.32342877765313 / (z + 3) +
    -176.61502916214059 / (z + 4) +
    12.507343278686905 / (z + 5) +
    -0.13857109526572012 / (z + 6) +
    9.9843695780195716e-6 / (z + 7) +
    1.5056327351493116e-7 / (z + 8);

  const t = z + 7.5;
  return Math.sqrt(2 * Math.PI) * Math.pow(t, z + 0.5) * Math.exp(-t) * x;
}

// ===== FISHER INFORMATION MATRIX UTILITIES =====

// t-distribution critical values lookup table
const T_CRITICAL_VALUES: Record<number, Record<number, number>> = {
  1: { 0.9: 6.314, 0.95: 12.706, 0.99: 63.657 },
  2: { 0.9: 2.92, 0.95: 4.303, 0.99: 9.925 },
  3: { 0.9: 2.353, 0.95: 3.182, 0.99: 5.841 },
  4: { 0.9: 2.132, 0.95: 2.776, 0.99: 4.604 },
  5: { 0.9: 2.015, 0.95: 2.571, 0.99: 4.032 },
  6: { 0.9: 1.943, 0.95: 2.447, 0.99: 3.707 },
  7: { 0.9: 1.895, 0.95: 2.365, 0.99: 3.499 },
  8: { 0.9: 1.86, 0.95: 2.306, 0.99: 3.355 },
  9: { 0.9: 1.833, 0.95: 2.262, 0.99: 3.25 },
  10: { 0.9: 1.812, 0.95: 2.228, 0.99: 3.169 },
  11: { 0.9: 1.796, 0.95: 2.201, 0.99: 3.106 },
  12: { 0.9: 1.782, 0.95: 2.179, 0.99: 3.055 },
  13: { 0.9: 1.771, 0.95: 2.16, 0.99: 3.012 },
  14: { 0.9: 1.761, 0.95: 2.145, 0.99: 2.977 },
  15: { 0.9: 1.753, 0.95: 2.131, 0.99: 2.947 },
  16: { 0.9: 1.746, 0.95: 2.12, 0.99: 2.921 },
  17: { 0.9: 1.74, 0.95: 2.11, 0.99: 2.898 },
  18: { 0.9: 1.734, 0.95: 2.101, 0.99: 2.878 },
  19: { 0.9: 1.729, 0.95: 2.093, 0.99: 2.861 },
  20: { 0.9: 1.725, 0.95: 2.086, 0.99: 2.845 },
  21: { 0.9: 1.721, 0.95: 2.08, 0.99: 2.831 },
  22: { 0.9: 1.717, 0.95: 2.074, 0.99: 2.819 },
  23: { 0.9: 1.714, 0.95: 2.069, 0.99: 2.807 },
  24: { 0.9: 1.711, 0.95: 2.064, 0.99: 2.797 },
  25: { 0.9: 1.708, 0.95: 2.06, 0.99: 2.787 },
  30: { 0.9: 1.697, 0.95: 2.042, 0.99: 2.75 },
  40: { 0.9: 1.684, 0.95: 2.021, 0.99: 2.704 },
  50: { 0.9: 1.676, 0.95: 2.009, 0.99: 2.678 },
  60: { 0.9: 1.671, 0.95: 2.0, 0.99: 2.66 },
  80: { 0.9: 1.664, 0.95: 1.99, 0.99: 2.639 },
  100: { 0.9: 1.66, 0.95: 1.984, 0.99: 2.626 },
  120: { 0.9: 1.658, 0.95: 1.98, 0.99: 2.617 },
  9999: { 0.9: 1.645, 0.95: 1.96, 0.99: 2.576 }, // Infinity approximation
};

/**
 * Get t-critical value for given degrees of freedom and confidence level
 */
function getTCriticalValue(degreesOfFreedom: number, confidenceLevel: number): number {
  // Find the closest degrees of freedom in our table
  const availableDf = Object.keys(T_CRITICAL_VALUES)
    .map(Number)
    .sort((a, b) => a - b);

  let df = degreesOfFreedom;
  if (df > 120) {
    df = 9999; // Use infinity approximation
  } else {
    // Find closest available df
    df = availableDf.reduce((prev, curr) =>
      Math.abs(curr - degreesOfFreedom) < Math.abs(prev - degreesOfFreedom) ? curr : prev
    );
  }

  // Support arbitrary confidence levels by interpolating or using closest match
  let levelKey: number;

  if (confidenceLevel <= 0.9) {
    levelKey = 0.9;
  } else if (confidenceLevel >= 0.99) {
    levelKey = 0.99;
  } else if (Math.abs(confidenceLevel - 0.95) < 0.025) {
    // Close to 95%
    levelKey = 0.95;
  } else if (confidenceLevel < 0.95) {
    // Interpolate between 90% and 95%
    const t90 = T_CRITICAL_VALUES[df][0.9];
    const t95 = T_CRITICAL_VALUES[df][0.95];
    const weight = (confidenceLevel - 0.9) / (0.95 - 0.9);
    return t90 + weight * (t95 - t90);
  } else {
    // Interpolate between 95% and 99%
    const t95 = T_CRITICAL_VALUES[df][0.95];
    const t99 = T_CRITICAL_VALUES[df][0.99];
    const weight = (confidenceLevel - 0.95) / (0.99 - 0.95);
    return t95 + weight * (t99 - t95);
  }

  return T_CRITICAL_VALUES[df][levelKey];
}

/**
 * Invert a 2x2 matrix using the analytical formula
 */
function invert2x2Matrix(matrix: number[][]): number[][] {
  const [[a, b], [c, d]] = matrix;
  const determinant = a * d - b * c;

  if (Math.abs(determinant) < 1e-12) {
    throw new Error('Matrix is singular and cannot be inverted');
  }

  return [
    [d / determinant, -b / determinant],
    [-c / determinant, a / determinant],
  ];
}

/**
 * Calculate Fisher Information Matrix for Weibull regression
 * Based on the linearized form: y = βx + intercept
 */
function calculateFisherInformation(x: number[]): number[][] {
  const n = x.length;
  const sumX = x.reduce((sum, xi) => sum + xi, 0);
  const sumXX = x.reduce((sum, xi) => sum + xi * xi, 0);

  // Fisher Information Matrix = X^T * X where X is design matrix
  // For linear regression: [[n, sumX], [sumX, sumXX]]
  return [
    [n, sumX],
    [sumX, sumXX],
  ];
}

/**
 * Calculate parameter covariance matrix
 */
function calculateParameterCovariance(
  fisherMatrix: number[][],
  residualVariance: number
): number[][] {
  const invFisher = invert2x2Matrix(fisherMatrix);

  // Covariance matrix = σ² * (X^T * X)^(-1)
  return [
    [residualVariance * invFisher[0][0], residualVariance * invFisher[0][1]],
    [residualVariance * invFisher[1][0], residualVariance * invFisher[1][1]],
  ];
}

/**
 * Calculate standard errors from covariance matrix
 */
function calculateStandardErrors(covarianceMatrix: number[][]): {
  shapeStdError: number;
  scaleStdError: number;
} {
  const shapeVariance = covarianceMatrix[0][0]; // Variance of slope (shape parameter)
  const interceptVariance = covarianceMatrix[1][1]; // Variance of intercept

  return {
    shapeStdError: Math.sqrt(Math.max(0, shapeVariance)),
    scaleStdError: Math.sqrt(Math.max(0, interceptVariance)), // Will be transformed later
  };
}

/**
 * Calculate confidence intervals for Weibull parameters and predictions using linearised approach
 */
function calculateConfidenceIntervals(
  operationTimes: number[],
  shape: number,
  scale: number,
  x: number[],
  y: number[],
  confidenceLevel: number = 0.95
): {
  parameterCI: {
    shapeCI: { lower: number; upper: number };
    scaleCI: { lower: number; upper: number };
  };
  predictionBands: { lower: { x: number; y: number }[]; upper: { x: number; y: number }[] };
  statistics: { standardErrors: { shape: number; scale: number }; degreesOfFreedom: number };
} {
  const n = operationTimes.length;
  const degreesOfFreedom = n - 2; // Two parameters estimated

  // Calculate basic statistics for regression
  const xMean = mean(x);
  const yMean = mean(y);

  // Calculate residual variance
  const yPredicted = x.map((xi) => shape * xi + (yMean - shape * xMean));
  const residualSumSquares = y.reduce((sum, yi, i) => sum + Math.pow(yi - yPredicted[i], 2), 0);
  const residualVariance = residualSumSquares / degreesOfFreedom;
  const residualStdError = Math.sqrt(residualVariance);

  // Calculate Fisher Information Matrix and covariance for parameter CIs
  const fisherMatrix = calculateFisherInformation(x);
  const covarianceMatrix = calculateParameterCovariance(fisherMatrix, residualVariance);
  const standardErrors = calculateStandardErrors(covarianceMatrix);

  // Get t-critical value
  const tCritical = getTCriticalValue(degreesOfFreedom, confidenceLevel);

  // Calculate parameter confidence intervals
  const shapeMarginOfError = tCritical * standardErrors.shapeStdError;
  const scaleStdErrorTransformed = (standardErrors.scaleStdError / shape) * scale;
  const scaleMarginOfError = tCritical * scaleStdErrorTransformed;

  const parameterCI = {
    shapeCI: {
      lower: Math.max(0.01, shape - shapeMarginOfError),
      upper: shape + shapeMarginOfError,
    },
    scaleCI: {
      lower: Math.max(0.01, scale - scaleMarginOfError),
      upper: scale + scaleMarginOfError,
    },
  };

  // Use linearised regression confidence intervals for prediction bands
  const minTime = min(operationTimes);
  const maxTime = max(operationTimes);
  const timeRange = Array.from({ length: 50 }, (_, i) => minTime + (i / 49) * (maxTime - minTime));

  // Calculate sum of squares for x for CI interval
  const xSumSquares = x.reduce((sum, xi) => sum + Math.pow(xi - xMean, 2), 0);

  // Regression intercept: c = yMean - shape * xMean
  const intercept = yMean - shape * xMean;

  // Calculate confidence bands using a single pass to ensure alignment
  const predictionBands = {
    lower: [] as { x: number; y: number }[],
    upper: [] as { x: number; y: number }[],
  };

  for (const t of timeRange) {
    const xPoint = Math.log(t);

    // Predicted y value at this x point
    const yHat = shape * xPoint + intercept;

    // Standard error of prediction at this point
    // This accounts for both the regression uncertainty and residual variance
    const standardErrorPrediction =
      residualStdError * Math.sqrt(1 / n + Math.pow(xPoint - xMean, 2) / xSumSquares);

    // Calculate confidence bounds in Weibull coordinate space (y-space)
    const yLower = yHat - tCritical * standardErrorPrediction;
    const yUpper = yHat + tCritical * standardErrorPrediction;

    // Transform back to probability space: F = 1 - exp(-exp(y))
    // Ensure bounds are valid (probabilities between 0 and 1)
    const probLower = Math.max(0, Math.min(1, 1 - Math.exp(-Math.exp(yLower))));
    const probUpper = Math.max(0, Math.min(1, 1 - Math.exp(-Math.exp(yUpper))));

    // Ensure monotonicity: lower bound ≤ upper bound
    const finalLower = Math.min(probLower, probUpper);
    const finalUpper = Math.max(probLower, probUpper);

    predictionBands.lower.push({ x: t, y: finalLower });
    predictionBands.upper.push({ x: t, y: finalUpper });
  }

  return {
    parameterCI,
    predictionBands,
    statistics: {
      standardErrors: {
        shape: standardErrors.shapeStdError,
        scale: scaleStdErrorTransformed,
      },
      degreesOfFreedom,
    },
  };
}

/**
 * Calculate Weibull parameters using least squares method with confidence intervals
 * Based on linearization: ln(-ln(1-F)) = β*ln(t) - β*ln(η)
 */
export function calculateWeibullParameters(
  operationTimes: number[],
  confidenceLevel: number = 0.95,
  failureRecords?: Array<{
    case_id: string;
    turbine_id: string;
    site_name: string;
    component_major: string;
    component_minor: string;
    failure_mode: string;
    outcome_name: string;
    failure_date: string;
    operation_time_years: number;
    turbine_model: string;
    turbine_name: string;
  }>
): WeibullAnalysisResult {
  if (operationTimes.length < 3) {
    throw new Error('Need at least 3 data points for Weibull analysis');
  }

  // Remove any zero or negative values and sort, keeping track of indices
  const validData = operationTimes
    .map((time, index) => ({ time, index, record: failureRecords?.[index] }))
    .filter((item) => item.time > 0)
    .sort((a, b) => a.time - b.time);

  if (validData.length < 3) {
    throw new Error('Need at least 3 positive operation times');
  }

  const validTimes = validData.map((item) => item.time);
  const validRecords = validData.map((item) => item.record); // Keep all records in sorted order, including undefined
  const n = validTimes.length;

  // Calculate plotting positions (median rank)
  const plottingPositions = validTimes.map((_, i) => (i + 1 - 0.3) / (n + 0.4));

  // Transform to Weibull coordinates
  const x = validTimes.map((t) => Math.log(t));
  const y = plottingPositions.map((p) => Math.log(-Math.log(1 - p)));

  // Linear regression: y = mx + b where m = β and b = -β*ln(η)
  const xMean = mean(x);
  const yMean = mean(y);

  const numerator = x.reduce((sum, xi, i) => sum + (xi - xMean) * (y[i] - yMean), 0);
  const denominator = x.reduce((sum, xi) => sum + Math.pow(xi - xMean, 2), 0);

  const slope = numerator / denominator; // β (shape parameter)
  const intercept = yMean - slope * xMean; // -β*ln(η)

  const shape = slope;
  const scale = Math.exp(-intercept / slope); // η (scale parameter)

  // Calculate R-squared
  const yPredicted = x.map((xi) => slope * xi + intercept);
  const ssRes = y.reduce((sum, yi, i) => sum + Math.pow(yi - yPredicted[i], 2), 0);
  const ssTot = y.reduce((sum, yi) => sum + Math.pow(yi - yMean, 2), 0);
  const rSquared = 1 - ssRes / ssTot;

  // Generate fitted line for plotting
  const minTime = min(validTimes);
  const maxTime = max(validTimes);
  const timeRange = Array.from({ length: 50 }, (_, i) => minTime + (i / 49) * (maxTime - minTime));

  const fittedLine = timeRange.map((t) => ({
    x: t,
    y: 1 - Math.exp(-Math.pow(t / scale, shape)), // CDF: F(t) = 1 - exp(-(t/η)^β)
  }));

  // Calculate summary statistics
  const meanLife = scale * gamma(1 + 1 / shape);
  const medianLife = scale * Math.pow(Math.log(2), 1 / shape);
  const characteristicLife = scale; // 63.2% probability

  // Calculate confidence intervals if we have enough data
  let confidenceIntervals;
  let statistics;

  if (validTimes.length >= 5) {
    // Need at least 5 points for meaningful confidence intervals
    try {
      confidenceIntervals = calculateConfidenceIntervals(
        validTimes,
        shape,
        scale,
        x,
        y,
        confidenceLevel
      );
      statistics = {
        standardErrors: confidenceIntervals.statistics.standardErrors,
        confidenceLevel,
        degreesOfFreedom: confidenceIntervals.statistics.degreesOfFreedom,
      };
    } catch (error) {
      console.warn('Could not calculate confidence intervals:', (error as Error).message);
    }
  }

  return {
    parameters: {
      shape,
      scale,
      rSquared,
      shapeCI: confidenceIntervals?.parameterCI.shapeCI,
      scaleCI: confidenceIntervals?.parameterCI.scaleCI,
    },
    plotData: {
      operationTimes: validTimes,
      probabilities: plottingPositions,
      fittedLine,
      failureRecords: validRecords, // Always include to maintain index alignment
      confidenceBands: confidenceIntervals?.predictionBands,
    },
    summary: {
      sampleSize: validTimes.length,
      meanLife,
      medianLife,
      characteristicLife,
    },
    statistics,
  };
}

/**
 * Calculate reliability at given time
 */
export function calculateReliability(time: number, shape: number, scale: number): number {
  return Math.exp(-Math.pow(time / scale, shape));
}

/**
 * Calculate failure rate (hazard function) at given time
 */
export function calculateFailureRate(time: number, shape: number, scale: number): number {
  return (shape / scale) * Math.pow(time / scale, shape - 1);
}

/**
 * Check if record represents an ONYX sourced observation
 * ONYX = record source === 'onyx_data'
 */
export function isONYXData(record: FailureRecord): boolean {
  const source = 'onyx_data';
  return (
    record.record_source === source
  );
}

/**
 * Check if a failure record represents a failure observation (Not Censored and not ONYX)
 * failure = record source === 'cust_data'
 */
export function isCustomerData(record: FailureRecord): boolean {
  const source = 'cust_data';
  return (
    record.record_source === source
  );
}

/**
 * Check if the record is a failure record from the Customer Data
 */
export function isCustomerFailure(record: FailureRecord): boolean {
  return isCustomerData(record) && !record.is_censor;
}

/**
 * Check if the record is a failure record from the ONYX Data
 */
export function isONYXFailure(record: FailureRecord): boolean {
  return isONYXData(record) && !record.is_censor;
}



/**
 * Check if the record is a failure record
 */
export function isFailureObservation(record: FailureRecord): boolean {
  return !record.is_censor;
}

/**
 * Check if the record is a Censor record from the ONYX Data
 */
export function isONYXCensor(record: FailureRecord): boolean {
  return isONYXData(record) && record.is_censor;
}
